import React, {useState, useEffect} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Grid,
    InputAdornment,
    Chip,
    Box,
    Typography,
    Divider,
    Autocomplete
} from '@mui/material';
import {Save, X} from 'react-feather';

const ServiceProviderDialog = ({open, provider, onClose, onSave, problemTypes}) => {
    const [formData, setFormData] = useState({
        id: null,
        first_name: '',
        last_name: '',
        specialization: '',
        email: '',
        phone_number: '',
        description: '',
        hourly_rate: '',
        is_active: true,
        problem_type_ids: []
    });

    const [errors, setErrors] = useState({});

    // Initialize form data when provider changes
    useEffect(() => {
        if (provider) {
            setFormData({
                id: provider.provider_id,
                first_name: provider.first_name || '',
                last_name: provider.last_name || '',
                specialization: provider.specialization || '',
                email: provider.email || '',
                phone_number: provider.phone_number || '',
                description: provider.description || '',
                hourly_rate: provider.hourly_rate ? String(provider.hourly_rate) : '',
                is_active: provider.is_active !== false,
                problem_type_ids: provider.problem_type_ids || []
            });
        } else {
            setFormData({
                id: null,
                first_name: '',
                last_name: '',
                specialization: '',
                email: '',
                phone_number: '',
                description: '',
                hourly_rate: '',
                is_active: true,
                problem_type_ids: []
            });
        }
        setErrors({});
    }, [provider]);

    // Handle input change
    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Clear error for the field
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    // Handle switch change
    const handleSwitchChange = (e) => {
        setFormData({
            ...formData,
            is_active: e.target.checked
        });
    };

    // Handle problem type selection
    const handleProblemTypeChange = (event, newValue) => {
        setFormData({
            ...formData,
            problem_type_ids: newValue.map(pt => pt.id)
        });
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required';
        }

        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last name is required';
        }

        if (!formData.specialization.trim()) {
            newErrors.specialization = 'Specialization is required';
        }

/*        if (formData.email && formData.email.trim() !== '' && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        if (formData.hourly_rate && isNaN(parseFloat(formData.hourly_rate))) {
            newErrors.hourly_rate = 'Hourly rate must be a number';
        }*/

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle save
    const handleSave = () => {
        if (validateForm()) {
            const providerData = {
                ...formData,
                hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null
            };
            onSave(providerData, !provider);
        }
    };

    // Get selected problem types
    const getSelectedProblemTypes = () => {
        if (!formData.problem_type_ids || !Array.isArray(formData.problem_type_ids)) {
            return [];
        }

        return problemTypes.filter(pt => formData.problem_type_ids.includes(pt.id));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{py: 2, px: 3, bgcolor: '#F9FAFB'}}>
                {provider ? 'Edit Service Provider' : 'Add New Service Provider'}
            </DialogTitle>

            <DialogContent sx={{p: 3}}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="First Name *"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleInputChange}
                            error={!!errors.first_name}
                            helperText={errors.first_name}
                            sx={{mb: 2}}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Last Name *"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleInputChange}
                            error={!!errors.last_name}
                            helperText={errors.last_name}
                            sx={{mb: 2}}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth sx={{mb: 2}}>
                            <InputLabel id="specialization-label">Specialization *</InputLabel>
                            <Select
                                labelId="specialization-label"
                                name="specialization"
                                value={formData.specialization}
                                onChange={handleInputChange}
                                label="Specialization *"
                                error={!!errors.specialization}
                            >
                                <MenuItem value="System Support">IT-Orama Support</MenuItem>
                                <MenuItem value="Plumber">Plumber</MenuItem>
                                <MenuItem value="Electrician">Electrician</MenuItem>
                                <MenuItem value="Carpenter">Carpenter</MenuItem>
                                <MenuItem value="HVAC Specialist">HVAC Specialist</MenuItem>
                                <MenuItem value="Painter">Painter</MenuItem>
                                <MenuItem value="Landscaper">Landscaper</MenuItem>
                                <MenuItem value="General Maintenance">General Maintenance</MenuItem>
                                <MenuItem value="Property Manager">Property Manager</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/*REMOVED BECAUSE PRICES ARE DIFFERENT FOR DIFFERENT SERVICES*/}
                    {/*<Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Hourly Rate"
                            name="hourly_rate"
                            value={formData.hourly_rate}
                            onChange={handleInputChange}
                            error={!!errors.hourly_rate}
                            helperText={errors.hourly_rate}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            sx={{mb: 2}}
                        />
                    </Grid>*/}

                    {/*REMOVED BECAUSE NOT EVERYONE HAS AN EMAIL ADDRESS*/}
                    {/*<Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            sx={{mb: 2}}
                        />
                    </Grid>*/}

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            name="phone_number"
                            value={formData.phone_number}
                            onChange={handleInputChange}
                            sx={{mb: 2}}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{my: 1}}>
                            <Chip label="Problem Types" variant="outlined"/>
                        </Divider>

                        <Autocomplete
                            multiple
                            options={problemTypes}
                            getOptionLabel={(option) => option.problem_type}
                            value={getSelectedProblemTypes()}
                            onChange={handleProblemTypeChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Assign Problem Types"
                                    placeholder="Select problem types"
                                    fullWidth
                                    sx={{mt: 2}}
                                />
                            )}
                            renderTags={(selected, getTagProps) =>
                                selected.map((option, index) => (

                                    <Chip
                                        variant="outlined"
                                        label={option.problem_type}
                                        {...getTagProps({index})}
                                        sx={{m: 0.5}}
                                    />
                                ))
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            multiline
                            rows={3}
                            placeholder="Describe this provider's skills, experience, and expertise..."
                            sx={{mt: 2}}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{mt: 2}}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={handleSwitchChange}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {formData.is_active ? 'Active' : 'Inactive'}
                                    </Typography>
                                }
                            />
                            <Typography variant="caption" color="textSecondary" sx={{display: 'block', mt: 0.5}}>
                                Inactive providers won't appear in assignment menus for tickets
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2, bgcolor: '#F9FAFB'}}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    startIcon={<X size={16}/>}
                    sx={{borderRadius: '8px'}}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    startIcon={<Save size={16}/>}
                    sx={{borderRadius: '8px'}}
                >
                    {provider ? 'Update Provider' : 'Add Provider'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ServiceProviderDialog;